import { useCallback, useContext, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import history from '@history';
import { showMessage } from 'app/store/core/messageSlice';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import DoneIcon from '@mui/icons-material/Done';
import { Button, CircularProgress } from '@mui/material';
import { useRequest } from '@main/hooks';
import { setAuthentication } from 'app/store/auth';
import { resendCode, validateCodeAuth } from 'src/app/api/auth';
import { CodeValidator } from '@main/components';
import { BootstrapContext } from 'app/bootstrap/BootstrapContext';

const IconBadge = styled('div')(({ theme }) => ({
  className: 'badge flex items-start py-4 px-8 mx-8 rounded',
  '& .': {
    height: '8rem',
    width: 'auto',
  },
}));

const CODE_LENGHT = 4;
const DEFAULT_ATTEMPTS = 4;
const LABEL_MSG =
  'Para validar su acceso ingrese el código de confirmación que le fue enviado a su número personal de WhatsApp.';

export const SignValidate = () => {
  const { initializeSession } = useContext(BootstrapContext);

  const location = useLocation();
  const prevParams = location.state;
  const Dispatch = useDispatch();

  const { finalizeSession } = useContext(BootstrapContext);
  const [currentCode, setCurrentCode] = useState(0);
  const [tokenValidate, setTokenValidate] = useState(undefined);
  const [instructions, setInstructions] = useState(LABEL_MSG);
  const codeValidatorRef = useRef(null);

  const handleOnForbidden = async () => {
    await finalizeSession();
  };

  const handleOnError = async (message) => {
    Dispatch(showMessage({ message }));
  };

  const {
    makeRequest: tryValidate,
    response: responseValidate,
    isLoading: isLoadingValidate,
  } = useRequest(validateCodeAuth, {
    onForbidden: handleOnForbidden,
    onError: handleOnError,
  });

  const {
    makeRequest: tryResend,
    response: responseResend,
    isLoading: isLoadingResend,
  } = useRequest(resendCode, {
    onForbidden: handleOnForbidden,
    onError: handleOnError,
  });

  const handleTryVerify = (e) => {
    const form = {
      tokenValidate,
      code: currentCode,
    };
    tryValidate(form).then(({ isOk, successContent }) => {
      if (!isOk) {
        codeValidatorRef.current?.handleResetCode();
        codeValidatorRef.current?.handleRemoveAttempt();
        return;
      }
      Dispatch(showMessage({ message: 'Codigo de inicio de sesion validado correctamente' }));
      Dispatch(setAuthentication(successContent?.access_token));
      const timeout = setTimeout(() => {
        initializeSession();
      }, 1000);
    });
  };

  const handleResend = useCallback(() => {
    tryResend({ tokenValidate }).then(({ isOk, successContent }) => {
      if (!isOk) {
        Dispatch(showMessage({ message: 'Ocurrio un error al reenviar el codigo' }));
        return;
      }
      Dispatch(showMessage({ message: 'Codigo de verificacion reenviado' }));
      setTokenValidate(successContent?.tokenValidate);
    });
  }, [tokenValidate]);

  useLayoutEffect(() => {
    console.log(prevParams);
    if (prevParams === undefined || prevParams === null) {
      history.replace('/sign-in');
      return;
    }
    setInstructions(prevParams.message);
    setTokenValidate(prevParams.tokenValidate);
  }, [prevParams]);

  return (
    <div className="flex flex-col flex-auto items-center sm:justify-center min-w-0">
      <Paper className="max-w-[720px] sm:w-full sm:min-w-[540px] min-h-full sm:min-h-auto rounded-0 py-32 px-32 sm:p-48 sm:m-32 sm:rounded-2xl sm:shadow relative">
        <div className="w-full mx-auto sm:mx-16">
          <div className="flex space-x-16 w-full items-center justify-center sm:justify-start flex-row">
            <IconBadge>
              <img
                src="assets/images/brands/brand-extended-colored-001.svg"
                alt="logo de biometrics"
              />
            </IconBadge>
            <IconBadge>
              <img src="assets/images/brands/brand-extended-colored-002.svg" alt="Fortuna" />
            </IconBadge>
          </div>

          <Typography className="mt-32 text-3xl sm:text-4xl text-center sm:text-start font-semibold tracking-tight leading-tight">
            Verificación de acceso
          </Typography>
          <Typography className="mt-16 text-center sm:text-start">{instructions}</Typography>

          <CodeValidator
            ref={codeValidatorRef}
            onResend={handleResend}
            codeLenght={4}
            attempts={DEFAULT_ATTEMPTS}
            isLoadingResend={isLoadingResend}
            onOverflow={handleResend}
            onChange={setCurrentCode}
            // onBlocked={}
          />

          <div className="w-full h-full flex justify-end items-center">
            <div className="flex w-full flex-row flex-wrap justify-center items-center mt-32">
              <Button
                className="my-4 mx-8"
                onClick={() => history.replace('/')}
                variant="text"
                color="primary"
              >
                Cancelar
              </Button>
              <Button
                className="my-4 mx-8"
                onClick={handleTryVerify}
                startIcon={isLoadingValidate ? <CircularProgress size="1.8rem" /> : <DoneIcon />}
                variant="contained"
                color="secondary"
                disabled={currentCode.length < CODE_LENGHT}
              >
                Validar
              </Button>
            </div>
          </div>
        </div>
      </Paper>
    </div>
  );
};
